<template>
  <div class="action-bar__initial-row" data-testid="pdp-add-to-cart">
    <div
      v-if="deliveryOptions && deliveryOptions.length > 0"
      class="action-bar__initial-item action-bar__initial-item--delivery"
    >
      <div class="action-bar__initial-label">
        {{
          $textDictionary['pdp.shippingTime.label'] ?? 'pdp.shippingTime.label'
        }}
        <info-icon
          v-if="
            shippingTooltipDictionaryKey &&
            shippingTooltipHtml &&
            !showShippingTextAsPlainText
          "
          :tooltip-text="shippingTooltipHtml"
          :tooltip-max-width="350"
          tooltip-placement="top"
        />
      </div>
      <div v-if="isInitialLoading" class="action-bar__initial-field">
        <loading-skeleton style="width: 100%; height: 38px" />
      </div>
      <ul v-else class="action-bar__initial-shipping-list">
        <li
          v-for="option in deliveryOptions"
          :key="option.titleAddition"
          class="action-bar__initial-shipping-item"
        >
          <icon :name="option?.iconName ?? ''" size="small" />
          <span>{{ option.title }} {{ option.titleAddition }}</span>
        </li>
      </ul>
    </div>
    <div class="action-bar__initial-item action-bar__initial-item--price">
      <div class="action-bar__initial-label">
        {{ $textDictionary['pdp.netPrice.label'] ?? 'pdp.netPrice.label' }}
      </div>
      <loading-skeleton
        v-if="isInitialLoading"
        style="width: 200px; height: 38px"
      />

      <price-element
        v-else
        :current-price="priceText"
        :previous-price="previousPriceText"
        :discount="discountPercentageText"
        alignment="oneline"
        :subline="showTariffInformation ? pdpTariffText : ''"
      />
    </div>
    <div class="action-bar__initial-item action-bar__initial-item--amount">
      <div class="action-bar__initial-label">
        {{ $textDictionary['pdp.amount.label'] ?? 'pdp.amount.label' }}
      </div>
      <loading-skeleton
        v-if="isInitialLoading"
        style="width: 100%; height: 38px"
      />
      <amount-input
        v-else
        :model-value="amount"
        :min="1"
        :max="maxAmount"
        :label="$textDictionary['pdp.amount.label'] ?? 'pdp.amount.label'"
        @update:model-value="$emit('update:amount', $event)"
      />
    </div>
    <div class="action-bar__initial-item action-bar__initial-item--actions">
      <AlertBox
        v-if="maintenanceMode"
        class="mt-4 maintenance-mode"
        icon="attention"
        :style="'danger'"
      >
        <template #default>
          <DictionaryRichText dictionary-key="maintenanceMode.text" />
        </template>
      </AlertBox>
      <ClientOnly v-else>
        <Button
          v-if="displayAddToCartButton"
          data-testid="pdp-add-to-cart-button"
          style-type="attraction"
          icon-type="icon-left"
          icon-name="add-shopping-cart"
          :icon-scale="1.4"
          size="medium"
          :active-state="isInitialLoading ? 'initial' : addToCartButtonState"
          :disabled="isInitialLoading || addToCartButtonDisabled"
          :states="{
            loading: {
              hideText: false,
              text:
                $textDictionary['pdp.addToCart.buttonState.loading'] ??
                'pdp.addToCart.buttonState.loading',
              ariaText:
                $textDictionary['pdp.addToCart.buttonState.loading'] ??
                'pdp.addToCart.buttonState.loading',
              resetDelay: -1
            },
            error: {
              hideText: false,
              text:
                $textDictionary['pdp.addToCart.buttonState.error'] ??
                'pdp.addToCart.buttonState.error',
              ariaText:
                $textDictionary['pdp.addToCart.buttonState.error'] ??
                'pdp.addToCart.buttonState.error',
              resetDelay: 2000
            },
            success: {
              hideText: false,
              text:
                $textDictionary['pdp.addToCart.buttonState.success'] ??
                'pdp.addToCart.buttonState.success',
              ariaText:
                $textDictionary['pdp.addToCart.buttonState.success'] ??
                'pdp.addToCart.buttonState.success',
              resetDelay: 2000
            }
          }"
          @click="
            $emit(
              'add-to-cart',
              loggedIn ? CartTypes.CUSTOMER_CART : CartTypes.CART
            )
          "
        >
          {{ $textDictionary['pdp.addToCart.label'] ?? 'pdp.addToCart.label' }}
        </Button>
        <Button
          :style-type="
            !displayAddToCartButton ? 'attraction' : 'attraction-secondary'
          "
          size="medium"
          :active-state="isInitialLoading ? 'initial' : requestQuoteButtonState"
          :disabled="isInitialLoading || requestQuoteButtonDisabled"
          :states="{
            loading: {
              hideText: false,
              text:
                $textDictionary['pdp.requestButton.buttonState.loading'] ??
                'pdp.requestButton.buttonState.loading',
              ariaText:
                $textDictionary['pdp.requestButton.buttonState.loading'] ??
                'pdp.requestButton.buttonState.loading',
              resetDelay: -1
            },
            error: {
              hideText: false,
              text:
                $textDictionary['pdp.requestButton.buttonState.error'] ??
                'pdp.requestButton.buttonState.error',
              ariaText:
                $textDictionary['pdp.requestButton.buttonState.error'] ??
                'pdp.requestButton.buttonState.error',
              resetDelay: 2000
            },
            success: {
              hideText: false,
              text:
                $textDictionary['pdp.requestButton.buttonState.success'] ??
                'pdp.requestButton.buttonState.success',
              ariaText:
                $textDictionary['pdp.requestButton.buttonState.success'] ??
                'pdp.requestButton.buttonState.success',
              resetDelay: 2000
            }
          }"
          @click="$emit('add-to-cart', CartTypes.REQUEST_CART)"
        >
          {{
            $textDictionary['pdp.requestButton.label'] ??
            'pdp.requestButton.label'
          }}
        </Button>
        <template #fallback>
          <div style="display: flex; gap: 8px; width: 100%">
            <loading-skeleton
              style="flex: 1; height: 40px; border-radius: 48px"
            />
            <loading-skeleton
              style="flex: 1; height: 40px; border-radius: 48px"
            />
          </div>
        </template>
      </ClientOnly>
    </div>
  </div>
  <div
    v-if="showSecondRow"
    class="action-bar__initial-row action-bar__initial-row--last"
  >
    <div
      v-if="shippingTooltipDictionaryKey && showShippingTextAsPlainText"
      class="action-bar__initial-item"
    >
      <DictionaryRichText
        :dictionary-key="shippingTooltipDictionaryKey"
        class="text--small mt-2"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  Icon,
  LoadingSkeleton,
  InfoIcon,
  Button,
  PriceElement,
  AmountInput,
  AlertBox
} from '@hypercodestudio/basler-components';
import type {
  BtnStateType,
  DeliveryOption
} from '@hypercodestudio/basler-components';

import { type CartType, CartTypes } from '~/stores/cartStore/model';
import { UNRESTRICTED_MAX_PURCHASE_QTY } from '~/utils/shop/maxPurchaseQuantityFromStoreConfig';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { useRichTextDictionary } from '~/composables/useRichTextDictionary';
import buildFeatureToggles from '~/utils/buildFeatureToggles';

const { $textDictionary, $globalPageSettings, $activeLocale } = useNuxtApp();

const maintenanceMode = computed(() => {
  return $globalPageSettings.value?.shopConfig?.maintenanceMode;
});

const showShippingTextAsPlainText = computed(() => {
  const featureToggles = buildFeatureToggles($globalPageSettings.value);
  const locales = featureToggles?.shippingTextAsPlainText ?? [];
  return locales.includes($activeLocale.value.webapp);
});

interface Props {
  deliveryOptions: DeliveryOption[];
  shippingTooltipDictionaryKey?: string;
  isInitialLoading: boolean;
  priceText: string;
  previousPriceText?: string;
  discountPercentageText?: string;
  showTariffInformation: boolean;
  pdpTariffText: string;
  amount: number;
  addToCartButtonState: BtnStateType;
  addToCartButtonDisabled: boolean;
  requestQuoteButtonState: BtnStateType;
  requestQuoteButtonDisabled: boolean;
  loggedIn: boolean;
  displayAddToCartButton: boolean;
  vscLink?: string;
  vscLinkText?: string;
  /** Upper bound for the amount input (from Magento storeConfig max-qty rules). */
  maxAmount?: number;
}

const props = withDefaults(defineProps<Props>(), {
  maxAmount: UNRESTRICTED_MAX_PURCHASE_QTY,
  shippingTooltipDictionaryKey: undefined,
  previousPriceText: undefined,
  discountPercentageText: undefined,
  vscLink: undefined,
  vscLinkText: undefined
});

const shippingTooltipRichTextDictionary = useRichTextDictionary(
  computed(() =>
    props.shippingTooltipDictionaryKey
      ? [props.shippingTooltipDictionaryKey]
      : []
  )
);

const shippingTooltipHtml = computed(() => {
  if (!props.shippingTooltipDictionaryKey) {
    return undefined;
  }

  const richText =
    shippingTooltipRichTextDictionary.value[props.shippingTooltipDictionaryKey];
  if (!richText) {
    return undefined;
  }

  return `<div class="rte-text">${documentToHtmlString(richText)}</div>`;
});

const showSecondRow = computed(() => {
  return !!(
    showShippingTextAsPlainText.value && props.shippingTooltipDictionaryKey
  );
});

defineEmits<{
  'update:amount': [value: number];
  'add-to-cart': [type: CartType];
}>();
</script>

<style scoped></style>
